/**
 * Meta tags del documento para SEO técnico (SPA sin SSR).
 * Usa VUE_APP_URL o VUE_APP_SITE_URL como base del canonical.
 * Títulos y descripciones de ruta: `src/locales/es-CL.json` / `en.json` → `routes.<route.name>`.
 */

import {
  productoImagenParaCatalogo,
  normalizeProductoImagenSrc,
  resolveCatalogMediaSrc,
} from '@/utils/productoImagenCatalogo.js';
import i18n from '@/i18n';

function shopName() {
  return (process.env.VUE_APP_SHOP_NAME || 'KOSEXA').trim();
}

function t(key, values) {
  if (!key) return '';
  try {
    if (i18n.global.te(key)) return i18n.global.t(key, values);
  } catch {
    /* noop */
  }
  return '';
}

function routeMetaField(routeName, field) {
  if (routeName == null || routeName === '') return '';
  return t(`routes.${String(routeName)}.${field}`);
}

/** Título para compartir inicio / redes (alineado con `public/index.html` vía build). */
function defaultShareTitle() {
  const raw = process.env.VUE_APP_SITE_SHARE_TITLE;
  if (raw != null && String(raw).trim()) return String(raw).trim();
  return t('seo.shareTitle');
}

function defaultShareDescription() {
  const raw = process.env.VUE_APP_SITE_SHARE_DESCRIPTION;
  if (raw != null && String(raw).trim()) return String(raw).trim();
  return t('seo.shareDescription');
}

function isLandingHome(route) {
  return route.name === 'LandingInicio' || route.name === 'LandingHome';
}

function siteBaseUrl() {
  const raw =
    process.env.VUE_APP_SITE_URL ||
    process.env.VUE_APP_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  return String(raw).replace(/\/$/, '');
}

function defaultDescription() {
  const raw = process.env.VUE_APP_DEFAULT_DESCRIPTION;
  if (raw != null && String(raw).trim()) return String(raw).trim();
  return t('seo.defaultDescription', { shop: shopName() });
}

function upsertMeta(attr, key, value) {
  if (value == null || value === '') return;
  const selector =
    attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function absoluteUrl(maybeRelative) {
  if (!maybeRelative) return '';
  const s = String(maybeRelative);
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  const base = siteBaseUrl();
  if (!base) return s;
  return `${base}${s.startsWith('/') ? '' : '/'}${s.replace(/^\//, '')}`;
}

/**
 * Aplica meta desde vue-router (route.meta).
 * Espera: title, description, opcional ogImage (absoluta o relativa).
 */
export function applyRouteMeta(route) {
  const m = route.meta || {};
  const brand = shopName();
  const routeName = route.name != null ? String(route.name) : '';
  const pageTitle = routeMetaField(routeName, 'title') || m.title || brand;
  const home = isLandingHome(route);
  const shareTitle = m.ogTitle || (home ? defaultShareTitle() : '');
  const documentTitle =
    shareTitle || (pageTitle === brand ? brand : `${pageTitle} · ${brand}`);
  document.title = documentTitle;

  const desc =
    routeMetaField(routeName, 'description') ||
    m.description ||
    (home ? defaultShareDescription() : defaultDescription());
  upsertMeta('name', 'description', desc);
  upsertMeta('name', 'robots', m.robots || 'index, follow');

  const base = siteBaseUrl();
  const pathOnly = route.fullPath.split('?')[0];
  const canonical =
    m.canonicalUrl || (base ? `${base}${pathOnly}` : '');
  upsertLink('canonical', canonical);

  upsertMeta('property', 'og:type', m.ogType || 'website');
  upsertMeta('property', 'og:site_name', 'Kosexa');
  const ogTitleVal = m.ogTitle || (home ? defaultShareTitle() : pageTitle);
  upsertMeta('property', 'og:title', ogTitleVal);
  upsertMeta('property', 'og:description', m.ogDescription || desc);
  if (canonical) upsertMeta('property', 'og:url', canonical);

  const ogImage =
    m.ogImage ||
    process.env.VUE_APP_OG_DEFAULT_IMAGE ||
    (home ? '/favicon.svg' : '');
  if (ogImage) {
    upsertMeta('property', 'og:image', absoluteUrl(ogImage));
    upsertMeta('property', 'og:image:alt', m.ogImageAlt || t('seo.ogImageAlt'));
  }

  upsertMeta('name', 'twitter:card', m.twitterCard || 'summary_large_image');
  upsertMeta('name', 'twitter:title', m.twitterTitle || ogTitleVal);
  upsertMeta('name', 'twitter:description', m.twitterDescription || desc);
  if (ogImage) {
    upsertMeta('name', 'twitter:image', absoluteUrl(ogImage));
  }
}

/**
 * Meta dinámico en ficha de producto (sobrescribe hasta la siguiente navegación).
 */
export function applyProductDocumentMeta(product) {
  if (!product) return;
  const brand = shopName();
  const pageTitle = product.nombre || 'Producto';
  document.title = `${pageTitle} · ${brand}`;
  const desc =
    (product.descripcion_corta || product.descripcion || '')
      .toString()
      .replace(/<[^>]+>/g, '')
      .slice(0, 160) || defaultDescription();
  upsertMeta('name', 'description', desc);
  upsertMeta('property', 'og:title', pageTitle);
  upsertMeta('property', 'og:description', desc);
  const apiImg = String(product.imagen || '').trim();
  const img = apiImg
    ? normalizeProductoImagenSrc(resolveCatalogMediaSrc(apiImg))
    : productoImagenParaCatalogo(product);
  if (img) {
    upsertMeta('property', 'og:image', absoluteUrl(img));
    upsertMeta('name', 'twitter:image', absoluteUrl(img));
  }
}

export function attachDocumentMetaToRouter(router) {
  router.afterEach((to) => {
    applyRouteMeta(to);
  });
}
